import * as React from 'react'
import {
  Show,
  SimpleShowLayout,
  TextField,
  ReferenceManyField,
  ReferenceField,
  SingleFieldList,
  ChipField,
  EditButton,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRedirect,
  fetchStart,
  fetchEnd,
} from 'react-admin'
import { useDispatch } from 'react-redux'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  Grid,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import { ParticipantAside } from './ParticipantAside'

const SoftDeleteButton = ({ record, style }) => {
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const dispatch = useDispatch()
  const notify = useNotify()
  const redirect = useRedirect()
  const dataProvider = useDataProvider()

  const handleSoftDelete = () => {
    setLoading(true)
    dispatch(fetchStart())
    dataProvider
      .create(`participant/${record.id}/soft-delete`, {
        data: { reason: 'admin_request' },
      })
      .then(() => {
        notify('Account is verwijderd en geanonimiseerd')
        redirect('/participant')
      })
      .catch(() => {
        notify('Er ging iets fout...', 'warning')
      })
      .finally(() => {
        setLoading(false)
        dispatch(fetchEnd())
        setOpen(false)
      })
  }

  return (
    <>
      <Button
        color="secondary"
        onClick={() => setOpen(true)}
        size="small"
        variant="outlined"
        disabled={loading || record?.status === 'deleted'}
        endIcon={<DeleteForeverIcon />}
        style={style}>
        Verwijder account
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Account verwijderen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Weet je zeker dat je dit account wilt verwijderen? Alle persoonlijke
            gegevens worden geanonimiseerd. Dit kan niet ongedaan worden
            gemaakt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Annuleren
          </Button>
          <Button
            onClick={handleSoftDelete}
            color="secondary"
            autoFocus
            disabled={loading}>
            Ja, verwijder account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const useDetailStyles = makeStyles((theme) => ({
  avatar: {
    width: 64,
    height: 64,
    fontSize: 24,
    backgroundColor: theme.palette.primary.main,
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fieldLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginBottom: 2,
  },
  fieldValue: {
    fontSize: '0.875rem',
  },
}))

const DetailField = ({ label, value, classes }) => (
  <Grid item xs={6}>
    <Typography className={classes.fieldLabel}>{label}</Typography>
    <Typography className={classes.fieldValue}>{value || '-'}</Typography>
  </Grid>
)

const BooleanItem = ({ label, value }) => (
  <Box display="flex" alignItems="center" mb={0.5}>
    {value ? (
      <CheckCircleIcon
        style={{ fontSize: 18, color: '#4caf50', marginRight: 6 }}
      />
    ) : (
      <CancelIcon style={{ fontSize: 18, color: '#bdbdbd', marginRight: 6 }} />
    )}
    <Typography variant="body2">{label}</Typography>
  </Box>
)

const ParticipantDetail = ({ record, resource, basePath }) => {
  const classes = useDetailStyles()
  if (!record) return null

  const initials = [record.firstName, record.lastName]
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Avatar src={record.avatarUrl} className={classes.avatar}>
          {!record.avatarUrl && initials}
        </Avatar>
        <Box ml={2}>
          <Typography variant="h5">
            {record.firstName} {record.lastName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {record.email}
          </Typography>
          {record.phoneNumber && (
            <Typography variant="body2" color="textSecondary">
              {record.phoneNumber}
            </Typography>
          )}
        </Box>
      </Box>

      <Divider />

      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom color="textSecondary">
          Persoonlijke gegevens
        </Typography>
        <Grid container spacing={2}>
          <DetailField
            label="Geboortejaar"
            value={record.yearOfBirth}
            classes={classes}
          />
          <DetailField
            label="Geslacht"
            value={record.gender}
            classes={classes}
          />
          <DetailField label="Stad" value={record.city} classes={classes} />
          <DetailField
            label="Postcode"
            value={record.postalCode}
            classes={classes}
          />
          <DetailField
            label="Bereik"
            value={record.rangeChoice}
            classes={classes}
          />
        </Grid>
      </Box>

      <Divider />

      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom color="textSecondary">
          Gevolgde sporten
        </Typography>
        <ReferenceManyField
          record={record}
          resource={resource}
          basePath={basePath}
          reference="participant_activity"
          target="participantID"
          link={false}
          sortable={false}>
          <SingleFieldList linkType={false}>
            <ReferenceField
              source="activityID"
              reference="activity"
              link={false}>
              <ChipField source="name" />
            </ReferenceField>
          </SingleFieldList>
        </ReferenceManyField>
      </Box>

      <Divider />

      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom color="textSecondary">
          Voorkeuren
        </Typography>
        <BooleanItem
          label="Nieuwsbrief algemeen"
          value={record.newsletterGeneral}
        />
        <BooleanItem label="Reactie-updates" value={record.commentsUpdate} />
        <BooleanItem
          label="Activiteiten-uitnodigingen"
          value={record.newsletterActivities}
        />
      </Box>

      <Divider />

      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom color="textSecondary">
          Bankgegevens
        </Typography>
        <Grid container spacing={2}>
          <DetailField
            label="Rekeningnummer"
            value={record.bankAccountNumber}
            classes={classes}
          />
          <DetailField
            label="Tenaamstelling"
            value={record.bankAccountName}
            classes={classes}
          />
        </Grid>
      </Box>
    </Box>
  )
}

const ParticipantShowActions = ({ isAdmin, basePath, data }) => {
  return (
    <TopToolbar>
      {isAdmin && <EditButton basePath={basePath} record={data} />}
      {isAdmin && data && (
        <SoftDeleteButton record={data} style={{ marginLeft: 8 }} />
      )}
    </TopToolbar>
  )
}

export const ParticipantShow = ({ permissions, ...props }) => {
  const permissionsJson = permissions && JSON.parse(permissions)
  const isAdmin = permissionsJson?.value?.includes('Administrator')
  return (
    <Show
      {...props}
      actions={<ParticipantShowActions isAdmin={isAdmin} />}
      aside={isAdmin ? <ParticipantAside /> : null}>
      {isAdmin ? (
        <ParticipantDetail />
      ) : (
        <SimpleShowLayout>
          <TextField source="firstName" />
          <TextField source="lastName" />
          <TextField source="email" />
          <TextField source="phoneNumber" />
        </SimpleShowLayout>
      )}
    </Show>
  )
}
